import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Settings, Save, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SiteSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    phone: "0522 580079",
    whatsapp: "+39 379 271 1214",
    email: "tab16.reggio@gmail.com",
    address: "Piazza Gioberti 4/B, angolo Via Emilia Santo Stefano 22/E, Reggio Emilia 42121",
    hero_title: "Tabaccheria N.16",
    hero_subtitle: "Punto Servizi & Rivenditore Svapo",
    hero_description: "Il tuo punto di riferimento per tabacchi, svapo, servizi al cittadino e molto altro",
    hero_image_url: "https://images.unsplash.com/photo-1601024445121-e5b82f020549?w=800&q=80"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Usa valori default se API fallisce
      toast({
        title: "Info",
        description: "Usando valori predefiniti. Puoi modificarli e salvare.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast({
        title: "Salvato!",
        description: "Impostazioni aggiornate con successo",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare le impostazioni",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/settings/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSettings({
        ...settings,
        hero_image_url: `${BACKEND_URL}${response.data.url}`
      });

      toast({
        title: "Immagine caricata!",
        description: "Ricorda di salvare le modifiche",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare l'immagine",
        variant: "destructive"
      });
    }
  };

  const updateSetting = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="site-settings">
        <div className="settings-loading">
          <p>Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="site-settings">
        <div className="settings-error">
          <p>Errore nel caricamento. Ricarica la pagina.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-settings">
      <div className="settings-header">
        <div>
          <h1 className="settings-title">
            <Settings className="w-8 h-8 inline mr-3" />
            Impostazioni Sito
          </h1>
          <p className="settings-subtitle">Modifica testi, immagini e contatti del sito</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="settings-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </div>

      <Tabs defaultValue="contatti" className="settings-tabs">
        <TabsList className="settings-tabs-list">
          <TabsTrigger value="contatti">Contatti</TabsTrigger>
          <TabsTrigger value="hero">Homepage</TabsTrigger>
          <TabsTrigger value="immagini">Immagini</TabsTrigger>
        </TabsList>

        <TabsContent value="contatti">
          <Card className="settings-card">
            <h3 className="settings-card-title">Informazioni Contatto</h3>
            
            <div className="settings-form">
              <div className="form-group">
                <Label>Telefono Fisso</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => updateSetting('phone', e.target.value)}
                  placeholder="0522 580079"
                />
              </div>

              <div className="form-group">
                <Label>WhatsApp</Label>
                <Input
                  value={settings.whatsapp}
                  onChange={(e) => updateSetting('whatsapp', e.target.value)}
                  placeholder="+39 379 271 1214"
                />
              </div>

              <div className="form-group">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => updateSetting('email', e.target.value)}
                  placeholder="tab16.reggio@gmail.com"
                />
              </div>

              <div className="form-group">
                <Label>Indirizzo Completo</Label>
                <Textarea
                  value={settings.address}
                  onChange={(e) => updateSetting('address', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="hero">
          <Card className="settings-card">
            <h3 className="settings-card-title">Sezione Homepage Principale</h3>
            
            <div className="settings-form">
              <div className="form-group">
                <Label>Titolo Principale</Label>
                <Input
                  value={settings.hero_title}
                  onChange={(e) => updateSetting('hero_title', e.target.value)}
                  placeholder="Tabaccheria N.16"
                />
              </div>

              <div className="form-group">
                <Label>Sottotitolo</Label>
                <Input
                  value={settings.hero_subtitle}
                  onChange={(e) => updateSetting('hero_subtitle', e.target.value)}
                  placeholder="Punto Servizi & Rivenditore Svapo"
                />
              </div>

              <div className="form-group">
                <Label>Descrizione</Label>
                <Textarea
                  value={settings.hero_description}
                  onChange={(e) => updateSetting('hero_description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="immagini">
          <Card className="settings-card">
            <h3 className="settings-card-title">Immagini Sito</h3>
            
            <div className="settings-image-section">
              <div className="current-image">
                <Label>Immagine Homepage Principale</Label>
                {settings.hero_image_url && (
                  <img 
                    src={settings.hero_image_url} 
                    alt="Hero" 
                    className="settings-preview-image"
                  />
                )}
              </div>

              <div className="image-upload-section">
                <p className="image-upload-instructions">
                  <ImageIcon className="w-5 h-5 inline mr-2" />
                  Carica una nuova immagine (JPG, PNG, WEBP - max 2MB)
                </p>
                <Input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="image-upload-input"
                />
                <p className="image-upload-note">
                  Formato consigliato: 1200x800px per migliori risultati
                </p>
              </div>

              <div className="image-url-section">
                <Label>Oppure inserisci URL immagine</Label>
                <Input
                  value={settings.hero_image_url || ''}
                  onChange={(e) => updateSetting('hero_image_url', e.target.value)}
                  placeholder="https://esempio.com/immagine.jpg"
                />
              </div>
            </div>
          </Card>

          <Card className="settings-card mt-6">
            <h3 className="settings-card-title">💡 Come aggiungere immagini</h3>
            <div className="settings-instructions">
              <p><strong>Metodo 1: Carica da Computer</strong></p>
              <ol>
                <li>Clicca "Scegli file" sopra</li>
                <li>Seleziona immagine dal tuo computer</li>
                <li>Clicca "Salva Modifiche" in alto</li>
              </ol>

              <p className="mt-4"><strong>Metodo 2: Usa URL Esterno</strong></p>
              <ol>
                <li>Carica immagine su servizio come Imgur, Google Photos</li>
                <li>Copia link immagine</li>
                <li>Incolla nel campo "URL immagine"</li>
                <li>Clicca "Salva Modifiche"</li>
              </ol>

              <p className="mt-4"><strong>Consigli:</strong></p>
              <ul>
                <li>Usa immagini del negozio, prodotti reali</li>
                <li>Dimensioni ottimali: 1200x800px</li>
                <li>Peso max: 500KB per caricamento veloce</li>
              </ul>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
